/**
 * FERP Portal - Facility Onboarding Readiness Page (DRR-184)
 *
 * Displays the 5W1H dimension readiness dashboard for wf-0-0-005 (facility onboarding).
 * Each dimension (WHO, WHAT, WHY, HOW, WHERE, WHEN) maps to a task group that must
 * complete before the facility goes live.
 *
 * Architecture:
 *   - Backend: wf-0-0-005 workflow + tg-0-1-001 through tg-0-1-006 task groups
 *   - Constellation: constellation-care-service-plan lifecycle.onboarding phase
 *   - DRR-184: dimension_coverage enforcement
 *
 * DRY: This 5W1H readiness pattern is reusable for any constellation-driven
 * workflow that requires dimension coverage. Other subgraphs can create their
 * own onboarding page using the same dimension → task group mapping.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: task status from backend queries
 * - Admin view layer only
 */

import {
  Alert,
  Badge,
  Card,
  Container,
  Group,
  Loader,
  Progress,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendar,
  IconClipboardCheck,
  IconCompass,
  IconInfoCircle,
  IconMapPin,
  IconRoute,
  IconShieldCheck,
  IconUser,
} from '@tabler/icons-react';
import {
  useOnboardingReadiness,
  READINESS_COLORS,
  READINESS_LABELS,
} from '@/hooks/useOnboardingReadiness';
import { GoLiveReadinessBadge } from '@/components/shared/GoLiveReadinessBadge';

// Map dimension IDs to icons (domain-agnostic)
const DIMENSION_ICONS: Record<string, typeof IconUser> = {
  WHO: IconUser,
  WHAT: IconClipboardCheck,
  WHY: IconShieldCheck,
  HOW: IconRoute,
  WHERE: IconMapPin,
  WHEN: IconCalendar,
};

export function FacilityOnboardingPage() {
  const {
    dimensions: dimensionStats,
    overallCompleted,
    overallTotal,
    overallPercent: overallPct,
    overallStatus,
    blockingRings,
    isReady: allGreen,
    loading,
    error,
  } = useOnboardingReadiness();

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Onboarding Status">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Facility Onboarding</Title>
            <Text c="dimmed" size="sm">
              DRR-184 — 5W1H dimension readiness for wf-0-0-005 facility onboarding workflow
            </Text>
          </div>
          <GoLiveReadinessBadge
            status={overallStatus}
            blockingRings={blockingRings}
            percent={overallPct}
            loading={loading}
            size="lg"
          />
        </Group>

        <Alert
          icon={<IconInfoCircle size={16} />}
          color={allGreen ? 'green' : 'blue'}
          title={allGreen ? 'All Dimensions Ready' : 'Onboarding In Progress'}
        >
          {allGreen
            ? 'All 6 dimensions are GREEN. The facility can proceed to go-live. Review each dimension before final approval.'
            : 'Each of the 6 dimensions (WHO, WHAT, WHY, HOW, WHERE, WHEN) must reach GREEN before the facility can go live. A missing dimension is a life-safety gap.'}
        </Alert>

        {loading && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        {/* Overall progress ring */}
        <Card withBorder p="lg">
          <Group justify="center" gap="xl">
            <RingProgress
              size={120}
              thickness={12}
              roundCaps
              sections={[
                { value: overallPct, color: allGreen ? 'green' : overallPct >= 50 ? 'yellow' : 'red' },
              ]}
              label={
                <Text ta="center" fw={700} size="lg">
                  {overallPct}%
                </Text>
              }
            />
            <div>
              <Text fw={600} size="lg">Overall Readiness</Text>
              <Text c="dimmed" size="sm">
                {overallCompleted} of {overallTotal} tasks completed across all dimensions
              </Text>
              <Group gap="xs" mt="xs">
                {dimensionStats.map((d) => (
                  <Badge
                    key={d.id}
                    variant="dot"
                    color={READINESS_COLORS[d.readiness]}
                    size="sm"
                  >
                    {d.id}
                  </Badge>
                ))}
              </Group>
            </div>
          </Group>
        </Card>

        {/* Per-dimension cards */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {dimensionStats.map((dim) => {
            const Icon = DIMENSION_ICONS[dim.id] ?? IconClipboardCheck;
            return (
              <Card key={dim.id} withBorder p="md">
                <Group justify="space-between" mb="sm">
                  <Group gap="sm">
                    <ThemeIcon variant="light" color={dim.color} size="lg">
                      <Icon size={20} />
                    </ThemeIcon>
                    <div>
                      <Text fw={600}>{dim.id}: {dim.label}</Text>
                      <Group gap={4}>
                        {dim.taskGroupRefs.map((ref) => (
                          <Text key={ref} size="xs" c="dimmed" ff="monospace">{ref}</Text>
                        ))}
                      </Group>
                    </div>
                  </Group>
                  <Badge
                    variant="light"
                    color={READINESS_COLORS[dim.readiness]}
                    size="sm"
                  >
                    {READINESS_LABELS[dim.readiness]}
                  </Badge>
                </Group>

                <Text size="sm" c="dimmed" mb="sm">{dim.description}</Text>

                <Progress
                  value={dim.percentComplete}
                  color={READINESS_COLORS[dim.readiness]}
                  size="sm"
                  mb="xs"
                />
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">{dim.completed}/{dim.total} tasks</Text>
                  {dim.blocked > 0 && (
                    <Badge variant="outline" color="red" size="xs">
                      {dim.blocked} blocked
                    </Badge>
                  )}
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>

        <Card withBorder p="md">
          <Group gap="sm" mb="xs">
            <ThemeIcon variant="light" color="indigo">
              <IconCompass size={18} />
            </ThemeIcon>
            <Text fw={600}>DRY Pattern: 5W1H Readiness</Text>
          </Group>
          <Text size="sm" c="dimmed">
            This dashboard reads task status from any workflow with dimension_coverage declarations.
            To create an onboarding dashboard for another domain: define task groups per dimension,
            declare dimension_coverage in the workflow YAML, and point this page at the new task group refs.
          </Text>
        </Card>
      </Stack>
    </Container>
  );
}

export default FacilityOnboardingPage;
